import type { Maybe } from '@adornis/base/utilTypes';
import { CustomContentExtension } from '@adornis/buildify/client/extensions/CustomContentExtension';
import type { Size } from '@adornis/buildify/client/globals/enums';
import type { BuildifyDocumentDefinition, BuildifyExtension } from '@adornis/buildify/client/globals/types';
import type { Base } from '@adornis/buildify/db/Base';
import { CustomContent } from '@adornis/buildify/db/CustomContent';
import { css } from '@adornis/chemistry/directives/css';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design';
import { XDialog } from '@adornis/dialog/x-dialog';
import { stateful, useObservable } from '@adornis/functional-lit/stateful';
import { webcomponent } from '@adornis/functional-lit/webcomponent';
import { html, nothing } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { firstValueFrom, from, timer } from 'rxjs';
import type { LASUser } from '../../../db/las-user';
import { Permissions } from '../../../db/permissions';
import { CreateCorrectionByEntityDialog } from '../../comments/client/CreateCorrectionByEntityDialog';
import type { Draft } from '../../drafts/db/Draft';

export const VisualizeBuildify = stateful(
  ({
    draftClassName,
    entityData,
    value,
    mode = 'view',
    getExtensions,
    customContent,
    onNavigateBackCallback,
    onSaveCallback,
    onAfterCorrectionCreated,
    addActionButtons = false,
    sizeBreakpoints,
    documentExtension,
    user,
    globalSettingsClassName,
  }: {
    draftClassName: string;
    entityData: {
      _id: string;
      _class: string;
    };
    globalSettingsClassName: string;
    value: Base[];
    mode: 'view' | 'edit';
    getExtensions: () => Promise<BuildifyExtension[]>;
    customContent?: typeof CustomContent;
    onNavigateBackCallback?: () => void;
    onSaveCallback?: () => Promise<void>;
    onAfterCorrectionCreated?: (draft: Draft) => void;
    addActionButtons?: boolean;
    user?: Maybe<LASUser>;
    sizeBreakpoints?: Record<Size, number>;
    documentExtension?: BuildifyDocumentDefinition;
  }) => {
    const extensions = useObservable(() => from(getExtensions()), []);
    const design = DesignSystem.currentTheme.getValue();

    if (!extensions) return html` <d-text> Extensions werden geladen... </d-text> `;

    return html`
      <div>
        ${mode === 'edit' || addActionButtons
          ? html`
              <d-flex horizontal space-between crossaxis-center ${css({ marginBottom: design.spacing.md })}>
                ${onNavigateBackCallback
                  ? html`
                      <d-button mode="outline" icon="arrow-left" @click=${() => onNavigateBackCallback()}>
                        zurück
                      </d-button>
                    `
                  : nothing}
                ${onSaveCallback
                  ? html`
                      <d-button
                        @click=${async () => {
                          await XDialog.waitFor(Promise.all([onSaveCallback(), firstValueFrom(timer(1000))]))
                            .then(res => {
                              XSnackbar.show('Gespeichert!');
                            })
                            .catch(err => {
                              XSnackbar.show('Speichern fehlgeschlagen...');
                            });
                        }}
                      >
                        Speichern
                      </d-button>
                    `
                  : nothing}
              </d-flex>
            `
          : nothing}
        ${mode === 'view' && !addActionButtons && user?.hasPermission(Permissions.RedakteurPermission.VIEW)
          ? html`
              ${AddCorrectionButton.render({ draftClassName, entityData, userID: user._id, onAfterCorrectionCreated })}
            `
          : nothing}

        <d-buildify
          .globalSettingsClassName=${globalSettingsClassName}
          .customContent=${{ ...CustomContentExtension, ...(customContent ? { class: customContent } : {}) }}
          ?editor=${mode === 'edit'}
          .value=${value}
          .extensions=${extensions}
          .sizeBreakpoints=${ifDefined(sizeBreakpoints)}
          .documentExtension=${ifDefined(documentExtension)}
        ></d-buildify>
      </div>
    `;
  },
);

export const AddCorrectionButton = webcomponent(
  'add-correction-button',
  ({
    entityData,
    draftClassName,
    userID,
    onAfterCorrectionCreated,
  }: {
    entityData: any;
    draftClassName: string;
    userID: string;
    onAfterCorrectionCreated?: (draft: Draft) => void;
  }) => html`
    <div class="flex justify-end">
      <button
        class="btn btn-accent btn-sm flex items-center gap-2"
        @click=${() => {
          CreateCorrectionByEntityDialog({
            draftClassName: draftClassName,
            userID,
            onAfterCorrectionCreated,
            _class: entityData._class,
            _id: entityData._id,
          });
        }}
      >
        <i class="fa-solid fa-pen-to-square text-sm"></i>
        <span>Korrektur hinzufügen</span>
      </button>
    </div>
  `,
  { shadowRoot: true },
);
